import React from 'react';
import dayjs from 'dayjs';
import { useAsync } from 'react-use';
import { api } from '~/api';
import { Order } from '~/models';
import { useStoreState } from 'easy-peasy';
import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import { getAddrInfo, routesIncluded, getAddrChoice } from '~/utils';

const EtaLabel = () => (
  <h4>
    <FormattedMessage
      id="marketrow-locationalert-field-eta-label"
      description="Label for `ETA` field on locationalert in Marketrow"
      defaultMessage="ETA"
    />
  </h4>
);

const SelectedLocationHeading = () => (
  <h3>
    <FormattedMessage
      id="marketrow-locationalert-header"
      description="Header for locationalert in Marketrow"
      defaultMessage="Selected location"
    />
  </h3>
);

export default ({ form, order, price, setPrice, schema }) => {
  const [loadingPrice, setLoadingPrice] = useState(false);
  const frometd = order?.etd?.from;
  const toetd = order?.etd?.to;
  const etaFreightTypes = ['port', 'zone'];

  const toaddr = form.getFieldValue('toaddr');
  const fromaddr = form.getFieldValue('fromaddr');
  const solution = useStoreState(state => state.auth.solution);

  useAsync(async () => {
    if (!!solution.freight && (toaddr || fromaddr)) {
      setLoadingPrice(true);
      const data = await api.getData(
        {
          type: 'orders',
          id: order.id,
          getResult: x => new Order(x.order),
        },
        { toaddr, fromaddr }
      );
      setLoadingPrice(false);
      setPrice(data.price.val);
    }
  }, [toaddr, fromaddr, solution]);

  if (
    (order.type === 'offer' &&
      !order.index &&
      etaFreightTypes.includes(schema?.toaddr?.freight) &&
      toetd &&
      solution.shippingroutes &&
      schema?.toaddr?.choices &&
      order?.fromaddr?.length) ||
    (order.type !== 'offer' &&
      !order.index &&
      // etaFreightTypes.includes(schema?.fromaddr?.freight) &&
      toetd &&
      solution.shippingroutes &&
      schema?.fromaddr?.choices &&
      order?.toaddr?.length)
  ) {
    const division = form.getFieldValue('division');

    const toaddrInfo = schema.toaddr?.choices
      ? getAddrInfo(toaddr)(getAddrChoice(division)(schema.toaddr.choices))
      : null;
    const fromaddrInfo = schema.fromaddr?.choices
      ? getAddrInfo(fromaddr)(getAddrChoice(division)(schema.fromaddr.choices))
      : null;

    if (
      (order.type === 'offer' && !toaddrInfo) ||
      (order.type !== 'offer' && !fromaddrInfo)
    ) {
      return null;
    }

    const matchingRoutes = solution.shippingroutes.filter(route => {
      let fromFreightIds;
      let toFreightIds;
      if (order.type === 'offer') {
        fromFreightIds = order.fromaddr.map(e => e?.[route.type]?.id);
        toFreightIds = toaddrInfo[2] ? [Object.values(toaddrInfo[2])[0]] : [];
      } else {
        fromFreightIds = fromaddrInfo[2]
          ? [Object.values(fromaddrInfo[2])[0]]
          : [];
        toFreightIds = order.toaddr.map(e => e?.[route.type]?.id);
      }
      return routesIncluded(fromFreightIds, toFreightIds)(route);
    });

    const sortedRoutes = matchingRoutes.sort(
      (route1, route2) => route1.duration - route2.duration
    );

    return sortedRoutes.length > 0 ? (
      <div className="counter-info-box mt-10 mb-10">
        <SelectedLocationHeading />
        <div className="counter-info-box-content">
          <div className="half">
            <h4>
              <FormattedMessage
                id="marketrow-locationalert-field-price-label"
                description="Label for `Price` field on locationalert in Marketrow"
                defaultMessage="Price"
              />
            </h4>
            <div className="price">
              {loadingPrice ? (
                <FormattedMessage
                  id="marketrow-locationalert-field-price-helptext-loading"
                  description="Helptext for `price` field on locationalert in Marketrow when price data is loading"
                  defaultMessage="Loading..."
                />
              ) : (
                price
              )}
            </div>
            <small>{order.price.unit}</small>
          </div>
          {sortedRoutes.map(route => (
            <div key={route.id} className="half">
              <EtaLabel />
              <div>
                {frometd && (
                  <>
                    <strong className="etd">
                      {dayjs(frometd)
                        .add(route.duration, 'day')
                        .format('DD MMM YYYY')}
                    </strong>{' '}
                    -{' '}
                  </>
                )}
                <strong className="etd">
                  {dayjs(toetd)
                    .add(route.duration, 'day')
                    .format('DD MMM YYYY')}
                </strong>
              </div>
              <small>
                <FormattedMessage
                  id="marketrow-locationalert-field-route-label"
                  description="Label for `Route` field on locationalert in Marketrow"
                  defaultMessage="From {fromAddress} To {toAddress}"
                  values={{
                    fromAddress: route.source.name,
                    toAddress: route.destination.name,
                  }}
                />
              </small>
            </div>
          ))}
        </div>
      </div>
    ) : null;
  }

  return null;
};
